import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Box, Grid, Paper, Stack, ToggleButtonGroup, ToggleButton, Typography, Alert, Button, Fab, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import withUser from '@components/withUser';
import type { WithUserProps } from '@components/withUser';
import StylistSelector from '@components/StylistSelector';
import LoadingSpinner from '@components/LoadingSpinner';
import NoStylist from '@components/NoStylist';
import ConfirmDialog from '@components/common/ConfirmDialog';
import {
  getReservations,
  getStylsitReservations,
  getBusiness,
  getPlans,
  createReservation,
  updateReservation,
  createStaffTimeSlots,
  deleteStaffTimeSlot,
  approveReservation,
  completeReservation,
  noShowReservation,
  cancelReservation,
  googleCalendarAuthRedirect,
  listGoogleCalendars,
  syncGoogleEvents,
  saveCalendarsToSync,
} from '@services/reservationService';

import ActionRail from '../components/reservation/v2/ActionRail';
import WeekCalendar from '../components/reservation/v2/WeekCalendar';
import AgendaView from '../components/reservation/v2/AgendaView';
import BookingDetailSheet from '../components/reservation/v2/BookingDetailSheet';
import NewBookingDialog from '../components/reservation/v2/NewBookingDialog';
import type { BookingPayload, BlockPayload } from '../components/reservation/v2/NewBookingDialog';
import SettingsDrawer from '../components/reservation/v2/SettingsDrawer';
import CalendarSelectionModal from '../components/reservation/CalendarSelectionModal';
import { isNativeIOS, syncIfAuthorized } from '@services/iosCalendarNative';
import type { Reservation, TimeSlot, CreateReservationParams, UpdateReservationParams, CancelReservationParams, TimeSlotData } from '@app-types/models/reservation';
import type { Stylist } from '@app-types/models/stylist';
import type { Plan } from '@app-types/models/plan';
import type { Business } from '@app-types/models/business';

interface GoogleCalendar {
  id: string;
  summary: string;
  primary?: boolean;
}

// The v2 fetch endpoints return bare arrays on success; tolerate a legacy { data: [...] } envelope too.
function unwrapList<T>(res: unknown): T[] {
  if (Array.isArray(res)) return res as T[];
  const d = (res as { data?: unknown } | null)?.data;
  return Array.isArray(d) ? (d as T[]) : [];
}

type ReservationsPageV2Props = WithUserProps;

// Redesigned reservations dashboard (v2). Runs alongside the original /reservations page.
// Desktop: split layout (action rail + calendar). Mobile: segmented toggle between the two.
const ReservationsPageV2: React.FC<ReservationsPageV2Props> = ({ user: _user }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { stylistId: routeStylistId } = useParams();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [stylists, setStylists] = useState<Stylist[]>([]);
  const [business, setBusiness] = useState<Business | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [selectedStylist, setSelectedStylist] = useState<Stylist | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [view, setView] = useState<'week' | 'agenda'>('week');      // 'week' | 'agenda'
  const [pane, setPane] = useState<'action' | 'calendar'>('calendar');  // mobile: 'action' | 'calendar'
  const [selected, setSelected] = useState<Reservation | null>(null); // booking open in detail sheet
  const [pendingCancel, setPendingCancel] = useState<Reservation | null>(null);
  const [newBookingOpen, setNewBookingOpen] = useState(false);
  const [newBookingPrefill, setNewBookingPrefill] = useState<{ date: Date; startTime: string | null } | null>(null);
  const [editReservation, setEditReservation] = useState<Reservation | null>(null);
  const [pendingRemoveBlock, setPendingRemoveBlock] = useState<TimeSlot | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [calModalOpen, setCalModalOpen] = useState(false);
  const [googleCalendars, setGoogleCalendars] = useState<GoogleCalendar[]>([]);
  const [selectedCalendars, setSelectedCalendars] = useState<string[]>([]);

  const fetchAll = useCallback(async () => {
    try {
      const [res, sty, biz, pl] = await Promise.all([
        getReservations({}),
        getStylsitReservations(),
        getBusiness(),
        getPlans(),
      ]);
      setReservations(unwrapList<Reservation>(res));
      const styList = unwrapList<Stylist>(sty);
      setStylists(styList);
      setBusiness(((biz as { data?: Business })?.data ?? (biz as unknown as Business)) ?? null);
      // /business/plans returns { plans: [...] }; tolerate a bare array too.
      setPlans(Array.isArray(pl) ? (pl as Plan[]) : (((pl as unknown) as { plans?: Plan[] }).plans ?? []));
      setSelectedStylist((prev) => styList.find((s) => s._id === prev?._id) || styList.find((s) => s._id === routeStylistId) || styList[0] || null);
    } catch (err) {
      console.error('Error loading reservations dashboard:', err);
      setError(t('reservationsV2.errorLoading'));
    }
  }, [t, routeStylistId]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await fetchAll();
      setIsLoading(false);
    })();
  }, [fetchAll]);

  const refetch = useCallback(async () => { await fetchAll(); }, [fetchAll]);

  const stylistReservations = useMemo(
    () => (selectedStylist ? reservations.filter((r) => r.staffId?._id === selectedStylist!._id) : reservations),
    [reservations, selectedStylist],
  );

  // Native iOS only: silently pull the device's Apple Calendar into blocked slots on load + app resume
  // (no permission prompt — that's the explicit "Connect Apple Calendar" button's job).
  useEffect(() => {
    if (!isNativeIOS() || !selectedStylist?._id) return undefined;
    const stylistId = selectedStylist._id;
    const run = async () => {
      try {
        const res = await syncIfAuthorized(stylistId);
        if (res && !res.skipped && res.modified) await refetch();
      } catch (err) {
        console.error('Auto iOS calendar sync failed:', err);
      }
    };
    run();
    // Re-sync when the app/tab returns to the foreground (fires on native resume too).
    const onVisible = () => { if (document.visibilityState === 'visible') run(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStylist?._id]);

  // ---- mutations (approve / complete / no-show / cancel) ----
  const runAction = async (fn: (id: string) => Promise<Reservation>, reservation: Reservation, successKey: string) => {
    try {
      await fn(reservation._id);
      toast.success(t(successKey));
      await refetch();
    } catch (err) {
      console.error('Reservation action failed:', err);
      toast.error(t('reservationsV2.errorAction'));
    }
  };

  const handleApprove = (r: Reservation) => runAction(approveReservation, r, 'reservationsV2.approvedToast');
  const handleComplete = (r: Reservation) => runAction(completeReservation, r, 'reservationsV2.completedToast');
  const handleNoShow = (r: Reservation) => runAction(noShowReservation, r, 'reservationsV2.noShowToast');

  const handleCancelConfirmed = async () => {
    const r = pendingCancel;
    const reason = cancelReason.trim();
    setPendingCancel(null);
    setCancelReason('');
    if (!r) return;
    try {
      await cancelReservation(r._id, (reason ? { cancelledReason: reason } : {}) as unknown as CancelReservationParams);
      toast.success(t('reservationsV2.cancelledToast'));
      await refetch();
    } catch (err) {
      console.error('Cancel failed:', err);
      toast.error(t('reservationsV2.errorAction'));
    }
  };

  const handleUpdateBooking = async (id: string, payload: BookingPayload) => {
    try {
      await updateReservation(id, payload as unknown as UpdateReservationParams);
      toast.success(t('reservationsV2.updatedToast'));
      setNewBookingOpen(false);
      setEditReservation(null);
      await refetch();
    } catch (err) {
      console.error('Update booking failed:', err);
      toast.error(t('reservationsV2.errorAction'));
    }
  };

  const handleEditBooking = (r: Reservation) => {
    setSelected(null);
    setNewBookingPrefill(null);
    setEditReservation(r);
    setNewBookingOpen(true);
  };

  const handleCreateBooking = async (payload: BookingPayload) => {
    try {
      await createReservation(payload as unknown as CreateReservationParams);
      toast.success(t('reservationsV2.createdBookingToast'));
      setNewBookingOpen(false);
      setNewBookingPrefill(null);
      await refetch();
    } catch (err) {
      console.error('Create booking failed:', err);
      toast.error(t('reservationsV2.errorAction'));
    }
  };

  const openNewBooking = (date: Date | null = null, startTime: string | null = null) => {
    setNewBookingPrefill(date ? { date, startTime } : null);
    setNewBookingOpen(true);
  };

  const handleBlockTime = async ({ start, end, title }: BlockPayload) => {
    if (!selectedStylist?._id) return;
    try {
      await createStaffTimeSlots({
        staffId: selectedStylist._id,
        timeSlot: { start, end, isOpen: false, title },
      } as unknown as TimeSlotData);
      toast.success(t('reservationsV2.blockCreatedToast'));
      setNewBookingOpen(false);
      setNewBookingPrefill(null);
      await refetch();
    } catch (err) {
      console.error('Block time failed:', err);
      toast.error(t('reservationsV2.errorAction'));
    }
  };

  const handleRemoveBlockConfirmed = async () => {
    const block = pendingRemoveBlock;
    setPendingRemoveBlock(null);
    if (!block || !selectedStylist?._id) return;
    try {
      await deleteStaffTimeSlot({ staffId: selectedStylist._id, timeSlotId: block._id as string });
      toast.success(t('reservationsV2.blockRemovedToast'));
      await refetch();
    } catch (err) {
      console.error('Remove block failed:', err);
      toast.error(t('reservationsV2.errorAction'));
    }
  };

  // ---- Google Calendar connect / selection ----
  const handleGoogleConnect = () => {
    if (!selectedStylist?._id) return;
    googleCalendarAuthRedirect(selectedStylist._id, `${window.location.origin}/reservations`);
  };

  const handleCalendarSync = async () => {
    try {
      await syncGoogleEvents(selectedStylist!._id, selectedCalendars);
      await saveCalendarsToSync(selectedStylist!._id, selectedCalendars);
      setCalModalOpen(false);
      const url = new URL(window.location as unknown as string);
      url.search = '';
      window.history.replaceState({}, '', url);
      await refetch();
    } catch (err) {
      console.error('Google sync failed:', err);
      toast.error(t('reservationsV2.errorAction'));
    }
  };

  // Return from Google OAuth: open the calendar-selection modal.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('action') === 'calendar-connected' && selectedStylist?._id) {
      (async () => {
        try {
          setGoogleCalendars((await listGoogleCalendars(selectedStylist!._id)) as unknown as GoogleCalendar[]);
          setCalModalOpen(true);
          setSettingsOpen(true);
        } catch (err) {
          console.error('List Google calendars failed:', err);
        }
      })();
    }
  }, [selectedStylist]);

  // Messaging is handled by the chat surface; deep-link there. (Kept light on v2.)
  const handleMessage = () => navigate('/notifications');

  const handleStylistSelect = (id: string | Stylist) => setSelectedStylist(stylists.find((s) => s._id === id) || null);

  if (isLoading) return (<Container><LoadingSpinner /></Container>);
  if (stylists.length === 0) return <NoStylist />;

  const rail = (
    <ActionRail
      reservations={reservations}
      stylistId={selectedStylist?._id}
      onApprove={handleApprove}
      onComplete={handleComplete}
      onNoShow={handleNoShow}
      onMessage={handleMessage}
      onViewAll={() => setSettingsOpen(true)}
    />
  );

  const calendarPanel = (
    <Paper elevation={1} sx={{ borderRadius: 3, overflow: 'hidden', minWidth: 0, width: '100%' }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2, pt: 1.5 }}>
        <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}>
          <ToggleButton value="week" sx={{ textTransform: 'none', px: 2 }}>{t('reservationsV2.week')}</ToggleButton>
          <ToggleButton value="agenda" sx={{ textTransform: 'none', px: 2 }}>{t('reservationsV2.agenda')}</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ flex: 1 }} />
        {!isMobile && (
          <Button variant="contained" size="small" onClick={() => openNewBooking()}>
            {t('reservationsV2.newBooking')}
          </Button>
        )}
      </Stack>
      {view === 'week' ? (
        <WeekCalendar
          reservations={stylistReservations}
          stylist={selectedStylist}
          business={business}
          onSelectBooking={setSelected}
          onCreateSlot={(date, startTime) => openNewBooking(date, startTime)}
          onRemoveBlock={(block) => setPendingRemoveBlock(block)}
        />
      ) : (
        <AgendaView reservations={stylistReservations} onSelectBooking={setSelected} />
      )}
    </Paper>
  );

  const pendingCount = reservations.filter(
    (r) => r.status === 'pending-for-approval' && (!selectedStylist?._id || r.staffId?._id === selectedStylist!._id),
  ).length;

  return (
    <Container maxWidth="xl" sx={{ pb: 6, overflow: 'hidden' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pt: 2, pb: 1, flexWrap: 'wrap', gap: 1, minWidth: 0 }}>
        <Typography variant="h5" fontWeight={700} sx={{ fontSize: { xs: 18, sm: 24 }, lineHeight: 1.2, minWidth: 0, flex: '1 1 auto' }}>{t('reservationsV2.title')}</Typography>
        <Button variant="outlined" color="secondary" startIcon={<SettingsIcon />} onClick={() => setSettingsOpen(true)} sx={{ flexShrink: 0 }}>
          {t('reservationsV2.settings')}
        </Button>
      </Stack>

      <Box sx={{ mb: 2 }}>
        <StylistSelector
          stylists={stylists}
          selectedStylistId={selectedStylist?._id}
          onStylistSelect={handleStylistSelect}
          layout="wrap"
          showEmail={false}
          variant="id"
        />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {isMobile ? (
        <Box sx={{ minWidth: 0, width: '100%' }}>
          <ToggleButtonGroup
            fullWidth exclusive size="small" value={pane}
            onChange={(_, v) => v && setPane(v)}
            sx={{ mb: 2, bgcolor: 'secondary.main', p: 0.5, borderRadius: 2, '& .MuiToggleButton-root': { border: 0, borderRadius: 1.5, textTransform: 'none', fontWeight: 600 } }}
          >
            <ToggleButton value="action">
              {t('reservationsV2.action')}
              {pendingCount > 0 && (
                <Box component="span" sx={{ ml: 1, bgcolor: 'primary.main', color: '#fff', fontSize: 11, fontWeight: 700, borderRadius: 999, px: 0.75 }}>{pendingCount}</Box>
              )}
            </ToggleButton>
            <ToggleButton value="calendar">{t('reservationsV2.calendar')}</ToggleButton>
          </ToggleButtonGroup>
          {pane === 'action' ? rail : calendarPanel}
        </Box>
      ) : (
        <Grid container spacing={2.5} alignItems="flex-start">
          <Grid size={{ md: 4, lg: 3.5 }}>{rail}</Grid>
          <Grid size={{ md: 8, lg: 8.5 }}>{calendarPanel}</Grid>
        </Grid>
      )}

      <BookingDetailSheet
        reservation={selected}
        onClose={() => setSelected(null)}
        onEdit={handleEditBooking}
        onApprove={handleApprove}
        onComplete={handleComplete}
        onNoShow={handleNoShow}
        onCancel={(r) => setPendingCancel(r)}
        onMessage={handleMessage}
      />

      {/* Cancel with an optional reason */}
      <Dialog open={!!pendingCancel} onClose={() => { setPendingCancel(null); setCancelReason(''); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{t('reservationsV2.confirmCancelTitle')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>{t('reservationsV2.confirmCancelPrompt')}</Typography>
          <TextField
            fullWidth multiline minRows={2} size="small"
            label={t('reservationsV2.cancelReasonLabel')}
            placeholder={t('reservationsV2.cancelReasonPlaceholder')}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="secondary" onClick={() => { setPendingCancel(null); setCancelReason(''); }}>{t('reservationsV2.no')}</Button>
          <Button variant="contained" color="error" onClick={handleCancelConfirmed}>{t('reservationsV2.yesCancel')}</Button>
        </DialogActions>
      </Dialog>

      <NewBookingDialog
        open={newBookingOpen}
        onClose={() => { setNewBookingOpen(false); setNewBookingPrefill(null); setEditReservation(null); }}
        onCreate={handleCreateBooking}
        onUpdate={handleUpdateBooking}
        onBlock={handleBlockTime}
        plans={plans}
        stylist={selectedStylist}
        business={business}
        existingReservations={stylistReservations}
        prefill={newBookingPrefill}
        editReservation={editReservation}
      />

      <ConfirmDialog
        open={!!pendingRemoveBlock}
        title={t('reservationsV2.removeBlockTitle')}
        message={t('reservationsV2.removeBlockPrompt', { title: pendingRemoveBlock?.title || '' })}
        onConfirm={handleRemoveBlockConfirmed}
        onCancel={() => setPendingRemoveBlock(null)}
      />

      {isMobile && (
        <Fab
          color="primary"
          aria-label={t('reservationsV2.newBooking')}
          onClick={() => openNewBooking()}
          sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1200 }}
        >
          <AddIcon />
        </Fab>
      )}

      <SettingsDrawer
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        stylist={selectedStylist}
        onRefresh={refetch}
        onGoogleConnect={handleGoogleConnect}
      />

      <CalendarSelectionModal
        open={calModalOpen}
        calendars={googleCalendars}
        selectedCalendars={selectedCalendars}
        onSelect={setSelectedCalendars}
        onClose={() => setCalModalOpen(false)}
        onSync={handleCalendarSync}
      />
    </Container>
  );
};

const WrappedReservationsPageV2 = withUser(ReservationsPageV2);
export default WrappedReservationsPageV2;
